import * as React from "react";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";
import {
  AppBar,
  Card,
  Divider,
  TextField,
  IconButton,
  Grid,
  FormHelperText,
  Paper,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip
} from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { POST } from "APIclient";
import { GET, BASE_URL } from "APIclient";
import { toast } from "react-toastify";
import { DataGrid } from "@mui/x-data-grid";
import { GridActionsCellItem } from "@mui/x-data-grid";
import Tooltip from "@mui/material/Tooltip";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import { Replay as ReplayIcon } from "@mui/icons-material";
import { Snackbar, Alert } from "@mui/material";
import properties from "constants/ApplicationConstants";
import { useNavigate } from "react-router-dom";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AssignmentIcon from "@mui/icons-material/Assignment";
import EditIcon from "@mui/icons-material/Edit";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import ArticleIcon from "@mui/icons-material/Article";
import QuizIcon from "@mui/icons-material/Quiz";

const columns = [
  { field: "id", headerName: "#", width: 20 },
  {
    field: "applyDate",
    headerName: "Applied On",
    width: 115,
    headerAlign: "center",
    align: "center"
  },
  {
    field: "resignationNotice",
    headerName: "Notice Period",
    width: 125,
    headerAlign: "center",
    align: "center"
  },
  {
    field: "relievingDate",
    headerName: "Relieving Date",
    width: 135,
    headerAlign: "center",
    align: "center"
  },
  {
    field: "status",
    headerName: "Status",
    width: 90,
    renderCell: params => {
      const status = params.value.toLowerCase();
      let buttonColor;
      let statusText;

      switch (status) {
        case "pending":
          buttonColor = "warning";
          statusText = "Pending";
          break;
        case "approved":
          buttonColor = "success";
          statusText = "Approved";
          break;
        case "rejected":
          buttonColor = "error";
          statusText = "Rejected";
          break;
        case "cancelled":
          buttonColor = "error";
          statusText = "Cancelled";
          break;
        case "revoke granted":
          buttonColor = "warning";
          statusText = "Revoke Granted";
          break;
        case "revoked":
          buttonColor = "error";
          statusText = "Revoked";
          break;
      }

      return (
        <MDButton
          variant="contained"
          color={buttonColor}
          size="small"
          disabled
          sx={{
            textTransform: "capitalize",
            fontSize: "0.800rem",
            width: "70px",
            justifyContent: "center"
          }}
        >
          {statusText}
        </MDButton>
      );
    }
  },
  {
    field: "actions",
    headerName: "Action",
    width: 75,
    headerAlign: "center",
    align: "center",
    renderCell: params => {
      const status = params.row.status.toLowerCase();

      return (
        <div style={{ display: "flex", gap: "4px", padding: '12px' }}>
          {status === "revoke granted" && (
            <Tooltip title="Revoke Resignation" arrow>
              <GridActionsCellItem
                icon={<ReplayIcon />}
                label="Revoke"
                color="primary"
                onClick={() => params.row.handleRevokeResignation(params.row)}
              />
            </Tooltip>
          )}

          <Tooltip title="View Details" arrow>
            <GridActionsCellItem
              icon={<VisibilityIcon />}
              label="View"
              color="primary"
              onClick={() => params.row.handleView(params.row)}
            />
          </Tooltip>
        </div>
      );
    }
  }
];

// Create a separate exit forms column definition
const exitFormsColumn = {
  field: "exitProcess",
  headerName: "Exit Forms",
  width: 205,
  headerAlign: "center",
  align: "center",
  renderCell: (params) => {
    const exitClearanceStatus = params.row.exitClearanceStatus || "Not Started";
    const exitInterviewStatus = params.row.exitInterviewStatus || "Not Started";

    const getStatusColor = (status) => {
      switch (status) {
        case "Submitted":
          return "#4caf50";
        case "In Progress":
          return "#ff9800";
        default:
          return "#9e9e9e";
      }
    };

    const isSelected = (type) => params.row.selectedExitForm === type;

    const getHighlightStyle = (type) =>
      isSelected(type)
        ? {
          border: `2px solid ${getStatusColor(type === "clearance"
            ? exitClearanceStatus
            : exitInterviewStatus)}`,
          backgroundColor: "#e3f2fd",
        }
        : {};

    return (
      <div
        style={{
          display: "flex",
          gap: "4px",
          alignItems: "center",
          justifyContent: "center",
          padding: "12px 0",
        }}
      >
        {/* CLEARANCE */}
        <Chip
          icon={
            exitClearanceStatus === "Submitted"
              ? <CheckCircleIcon sx={{ fontSize: 16 }} />
              : <PendingIcon sx={{ fontSize: 16 }} />
          }
          label="Clearance"
          size="small"
          sx={{
            backgroundColor: getStatusColor(exitClearanceStatus) + "20",
            color: getStatusColor(exitClearanceStatus),
            fontWeight: 500,
            fontSize: "0.75rem",
            height: "28px",
            cursor: "pointer",
            minWidth: "90px",
            ...getHighlightStyle("clearance"),
          }}
          onClick={(e) => {
            e.stopPropagation();
            params.row.selectedExitForm = "clearance";
            params.row.handleExitClearanceMenu(e, params.row);
          }}
        />

        {/* INTERVIEW */}
        <Chip
          icon={
            exitInterviewStatus === "Submitted"
              ? <CheckCircleIcon sx={{ fontSize: 16 }} />
              : <PendingIcon sx={{ fontSize: 16 }} />
          }
          label="Interview"
          size="small"
          sx={{
            backgroundColor: getStatusColor(exitInterviewStatus) + "20",
            color: getStatusColor(exitInterviewStatus),
            fontWeight: 500,
            fontSize: "0.75rem",
            height: "28px",
            cursor: "pointer",
            minWidth: "90px",
            ...getHighlightStyle("interview"),
          }}
          onClick={(e) => {
            e.stopPropagation();
            params.row.selectedExitForm = "interview";
            params.row.handleExitInterviewMenu(e, params.row);
          }}
        />
      </div>
    );
  },
};

export default function Resignation() {
  const empId = window.sessionStorage.getItem("empId");
  const theme = useTheme();
  const navigate = useNavigate();
  const [value, setValue] = React.useState(dayjs());
  const [reason, setReason] = React.useState("");
  const [reasonError, setReasonError] = React.useState(false);
  const [resignationData, setResignationData] = React.useState([]);

  const [openViewModal, setOpenViewModal] = React.useState(false);
  const [selectedResignationRequest, setSelectedResignationRequest] = React.useState(null);

  const [openRevokeModal, setOpenRevokeModal] = React.useState(false);
  const [revokeReason, setRevokeReason] = React.useState("");
  const [revokeReasonError, setRevokeReasonError] = React.useState(false);

  const [snackOpen, setSnackOpen] = React.useState(false);
  const [snackColor, setSnackColor] = React.useState("success");
  const [snackMessage, setSnackMessage] = React.useState("");

  const [newResignationSubmitted, setNewResignationSubmitted] = React.useState(false);

  // Exit Clearance Menu
  const [exitClearanceAnchorEl, setExitClearanceAnchorEl] = React.useState(null);
  const [selectedExitClearanceRow, setSelectedExitClearanceRow] = React.useState(null);
  const openExitClearanceMenu = Boolean(exitClearanceAnchorEl);

  // Exit Interview Menu
  const [exitInterviewAnchorEl, setExitInterviewAnchorEl] = React.useState(null);
  const [selectedExitInterviewRow, setSelectedExitInterviewRow] = React.useState(null);
  const openExitInterviewMenu = Boolean(exitInterviewAnchorEl);
  const [selectedExitForm, setSelectedExitForm] = React.useState(null);

  const [hasApprovedResignation, setHasApprovedResignation] = React.useState(false);


  const handleClose = () => {
    setSnackOpen(false);
  };

  // Exit Clearance Menu Handlers
  const handleExitClearanceMenu = (event, rowData) => {
    setExitClearanceAnchorEl(event.currentTarget);
    setSelectedExitClearanceRow(rowData);
  };

  const handleCloseExitClearanceMenu = () => {
    setExitClearanceAnchorEl(null);
    setSelectedExitClearanceRow(null);
  };

  // For Exit Clearance View/Preview
  const handleViewExitClearance = async () => {
    // if (selectedExitClearanceRow) {
    //   try {
    //     const token = localStorage.getItem("jwtToken") || sessionStorage.getItem("jwtToken");
    //     const url = `${BASE_URL}/api/exit/clearance/preview/${empId}`;

    //     const response = await fetch(url, {
    //       method: 'GET',
    //       headers: {
    //         'Authorization': `Bearer ${token}`,
    //         'Accept': 'application/pdf'
    //       },
    //       credentials: 'include'
    //     });

    //     if (!response.ok) {
    //       throw new Error(`Exit Clearance not yet submitted`);
    //     }

    //     const blob = await response.blob();

    //     if (blob.size === 0) {
    //       throw new Error("Received empty PDF file");
    //     }

    //     const fileURL = window.URL.createObjectURL(blob);
    //     window.open(fileURL, "_blank");

    //     // Clean up after a delay
    //     setTimeout(() => window.URL.revokeObjectURL(fileURL), 100);
    //   } catch (error) {
    //     console.error("Preview error:", error);
    //     setSnackMessage(
    //       error.message || "Failed to preview exit clearance form."
    //     );
    //     setSnackColor("error");
    //     setSnackOpen(true);
    //   }
    // }
    // handleCloseExitClearanceMenu();
    if (selectedExitClearanceRow) {
      localStorage.setItem("resignationEndDate", selectedExitClearanceRow.relievingDate);

      navigate("/exitClearanceForm", {
        state: {
          mode: "view",
          resignationId: selectedExitClearanceRow.resignationRequestId
        }
      });
    }
    handleCloseExitClearanceMenu();
  };

  const handleFillExitClearance = () => {
    if (selectedExitClearanceRow) {
      localStorage.setItem("resignationEndDate", selectedExitClearanceRow.relievingDate);

      navigate("/exitClearanceForm", {
        state: {
          mode: "edit",
          resignationId: selectedExitClearanceRow.resignationRequestId
        }
      });
    }
    handleCloseExitClearanceMenu();
  };

  const handleDownloadExitClearance = async () => {
    if (selectedExitClearanceRow) {
      try {
        const token = localStorage.getItem("jwtToken") || sessionStorage.getItem("jwtToken");
        const url = `${BASE_URL}/api/exit/clearance/download/${empId}`;

        // Use native fetch to properly handle binary data
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/pdf'
          },
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error(`Exit Clearance not yet submitted`);
        }

        // Get the blob directly from fetch
        const blob = await response.blob();

        console.log("Response blob type:", blob.type);
        console.log("Response blob size:", blob.size);

        // Check if blob has content
        if (blob.size === 0) {
          throw new Error("Received empty PDF file");
        }

        // Create download link
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `Exit_Clearance_${empId}_${dayjs().format("YYYYMMDD")}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up the URL object
        setTimeout(() => window.URL.revokeObjectURL(link.href), 100);

        setSnackMessage("Exit clearance form downloaded successfully!");
        setSnackColor("success");
        setSnackOpen(true);
      } catch (error) {
        console.error("Download error:", error);
        setSnackMessage(
          error.message || "Failed to download exit clearance form."
        );
        setSnackColor("error");
        setSnackOpen(true);
      }
    }
    handleCloseExitClearanceMenu();
  };


  // Exit Interview Menu Handlers
  const handleExitInterviewMenu = (event, rowData) => {
    setExitInterviewAnchorEl(event.currentTarget);
    setSelectedExitInterviewRow(rowData);
  };

  const handleCloseExitInterviewMenu = () => {
    setExitInterviewAnchorEl(null);
    setSelectedExitInterviewRow(null);
  };

  // For Exit Interview View/Preview
  // For Exit Interview View/Preview
  const handleViewExitInterview = async () => {
    // if (selectedExitInterviewRow) {
    //   try {
    //     const token = localStorage.getItem("jwtToken") || sessionStorage.getItem("jwtToken");
    //     const url = `${BASE_URL}/api/exit/interview/preview/${empId}`;

    //     const response = await fetch(url, {
    //       method: 'GET',
    //       headers: {
    //         'Authorization': `Bearer ${token}`,
    //         'Accept': 'application/pdf'
    //       },
    //       credentials: 'include'
    //     });

    //     if (!response.ok) {
    //       throw new Error(`Exit interview not yet submitted`);
    //     }

    //     const blob = await response.blob();

    //     if (blob.size === 0) {
    //       throw new Error("Received empty PDF file");
    //     }

    //     const fileURL = window.URL.createObjectURL(blob);
    //     window.open(fileURL, "_blank");

    //     // Clean up after a delay
    //     setTimeout(() => window.URL.revokeObjectURL(fileURL), 100);
    //   } catch (error) {
    //     console.error("Preview error:", error);
    //     setSnackMessage(
    //       error.message || "Failed to preview exit interview form."
    //     );
    //     setSnackColor("error");
    //     setSnackOpen(true);
    //   }
    // }
    // handleCloseExitInterviewMenu();

    if (selectedExitInterviewRow) {
      localStorage.setItem("resignationEndDate", selectedExitInterviewRow.relievingDate);

      navigate("/exitInterviewForm", {
        state: {
          mode: "view",
          resignationId: selectedExitInterviewRow.resignationRequestId
        }
      });
    }
    handleCloseExitInterviewMenu();
  };

  const handleFillExitInterview = () => {
    if (selectedExitInterviewRow) {
      localStorage.setItem("resignationEndDate", selectedExitInterviewRow.relievingDate);

      navigate("/exitInterviewForm", {
        state: {
          mode: "edit",
          resignationId: selectedExitInterviewRow.resignationRequestId
        }
      });
    }
    handleCloseExitInterviewMenu();
  };

  const handleDownloadExitInterview = async () => {
    if (selectedExitInterviewRow) {
      try {
        const token = localStorage.getItem("jwtToken") || sessionStorage.getItem("jwtToken");
        const url = `${BASE_URL}/api/exit/interview/download/${empId}`;

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/pdf'
          },
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error(`Exit interview not yet submitted`);
        }

        const blob = await response.blob();

        console.log("Response blob type:", blob.type);
        console.log("Response blob size:", blob.size);

        if (blob.size === 0) {
          throw new Error("Received empty PDF file");
        }

        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `Exit_Interview_${empId}_${dayjs().format("YYYYMMDD")}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => window.URL.revokeObjectURL(link.href), 100);

        setSnackMessage("Exit interview form downloaded successfully!");
        setSnackColor("success");
        setSnackOpen(true);
      } catch (error) {
        console.error("Download error:", error);
        setSnackMessage(
          error.message || "Failed to download exit interview form."
        );
        setSnackColor("error");
        setSnackOpen(true);
      }
    }
    handleCloseExitInterviewMenu();
  };

  const handleRevokeResignation = rowData => {
    setSelectedResignationRequest(rowData);
    setOpenRevokeModal(true);
  };

  const handleRevokeSubmit = async e => {
    e.preventDefault();
    if (!revokeReason) {
      setRevokeReasonError(true);
      return;
    }
    try {
      const url = `employee/resignation/revokedPermission/${selectedResignationRequest.resignationRequestId}`;
      const revokeData = {
        revokedReason: revokeReason
      };
      const response = await POST(url, revokeData);

      if (response.status === 200) {
        setSnackMessage(response.data.message);
        setSnackColor("success");
        setSnackOpen(true);
        const updatedData = resignationData.map(item => {
          if (item.resignationRequestId === selectedResignationRequest.resignationRequestId) {
            return {
              ...item,
              status: "Revoked"
            };
          }
          return item;
        });
        setResignationData(updatedData);
        setOpenRevokeModal(false);
      }
    } catch (error) {
      if (error.response) {
        if (
          error.response.status === 400 ||
          error.response.status === 401 ||
          error.response.status === 403
        ) {
          setSnackMessage(error.response.data.message);
          setSnackColor("error");
          setSnackOpen(true);
        } else {
          setSnackMessage("An error occurred while revoking the resignation.");
          setSnackColor("error");
          setSnackOpen(true);
          toast.error("An error occurred while revoking the resignation.");
        }
      }
    }
  };

  const handleViewDetails = rowData => {
    setSelectedResignationRequest(rowData);
    setOpenViewModal(true);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!reason.trim()) {
      setReasonError(true);
      return;
    }
    const resignationData = {
      empId: empId,
      applyDate: value.format("YYYY-MM-DD"),
      reason: reason
    };
    try {
      const url = `employee/applyResignation`;
      const response = await POST(url, resignationData);
      console.log(response);
      if (response.status === 200) {
        setSnackMessage(response.data.message);
        setSnackColor("success");
        setSnackOpen(true);
        setNewResignationSubmitted(true);
        setReason("");
      }
    } catch (error) {
      if (error.response) {
        if (
          error.response.status === 400 ||
          error.response.status === 401 ||
          error.response.status === 403
        ) {
          const errorMessage = error.response.data.message || "An error occurred.";
          setSnackMessage(errorMessage);
          setSnackColor("error");
          setSnackOpen(true);
        }
      } else {
        setSnackMessage("An error occurred while submitting the resignation request.");
        setSnackColor("error");
        setSnackOpen(true);
      }
    }
  };

  // Create dynamic columns based on approved status
  const dynamicColumns = React.useMemo(() => {
    if (hasApprovedResignation) {
      // Insert exitFormsColumn before the actions column
      const cols = [...columns];
      cols.splice(cols.length - 1, 0, exitFormsColumn);
      return cols;
    }
    return columns;
  }, [hasApprovedResignation]);

  React.useEffect(() => {
    const url = `employee/getResignationList/${empId}`;
    const fetchResignationData = async () => {
      try {
        const response = await GET(url, { params: { page: 0, size: 20 } });
        console.log("API Response:", response);

        if (
          response.status === 200 &&
          response.data &&
          Array.isArray(response.data.data.paginatedData)
        ) {
          const data = response.data.data.paginatedData;
          if (data.length === 0) {
            return;
          }

          const formattedData = data.map((item, index) => {
            const status = parseInt(item.status);
            let statusLabel = "";
            if (status === 1 && item.approved === 0 && item.revokedPermission === 0) {
              statusLabel = "Pending";
            } else if (item.approved === 1 && item.revokedPermission === 0) {
              statusLabel = "Approved";
            } else if (item.cancel === 1) {
              statusLabel = "Cancelled";
            } else if (status === 0 && item.approved === 2) {
              statusLabel = "Rejected";
            } else if (
              status === 1 &&
              item.approved === 1 &&
              item.revokedPermission === 1 &&
              item.revoked === 0
            ) {
              statusLabel = "Revoke Granted";
            } else if (
              status === 1 &&
              item.revoked === 0 &&
              item.revokedPermission === 1 &&
              item.approved === 0
            ) {
              statusLabel = "Revoke Granted";
            } else if (
              status === 0 &&
              item.revoked === 1 &&
              item.approved === 0 &&
              item.revokedPermission === 1
            ) {
              statusLabel = "Revoked";
            } else if (
              status === 0 &&
              item.revoked === 1 &&
              item.approved === 1 &&
              item.revokedPermission === 1
            ) {
              statusLabel = "Revoked";
            }

            let resignationNoticeFormatted = "N/A";
            if (item.resignationNotice && item.resignationNotice > 0) {
              resignationNoticeFormatted = `${item.resignationNotice} Month${item.resignationNotice > 1 ? "s" : ""
                }`;
            }

            const relievingDate = item.endDate ? dayjs(item.endDate).format("DD MMM YYYY") : "N/A";

            // Determine exit clearance status
            let exitClearanceStatus = "Not Started";
            if (statusLabel === "Approved" && item.exitClearanceSubmitted) {
              exitClearanceStatus = "Submitted";
            } else if (statusLabel === "Approved" && item.exitClearanceInProgress) {
              exitClearanceStatus = "In Progress";
            }

            // Determine exit interview status
            let exitInterviewStatus = "Not Started";
            if (statusLabel === "Approved" && item.exitInterviewSubmitted) {
              exitInterviewStatus = "Submitted";
            } else if (statusLabel === "Approved" && item.exitInterviewInProgress) {
              exitInterviewStatus = "In Progress";
            }

            return {
              id: index + 1,
              empId: item.empId,
              empName: `${item.firstName} ${item.lastName}`,
              resignationNotice: resignationNoticeFormatted,
              applyDate: dayjs(item.applyDate).format("DD MMM YYYY"),
              reason: item.reason || "No reason provided",
              status: statusLabel,
              relievingDate: relievingDate,
              resignationRequestId: item.resignRequestId,
              joinDate: dayjs(item.joinDate).format("DD MMM YYYY"),
              exitClearanceStatus: exitClearanceStatus,
              exitInterviewStatus: exitInterviewStatus,
              handleView: handleViewDetails,
              handleRevokeResignation: handleRevokeResignation,
              handleExitClearanceMenu: handleExitClearanceMenu,
              handleExitInterviewMenu: handleExitInterviewMenu
            };
          });

          // Check if any resignation is approved
          const hasApproved = formattedData.some(
            item => item.status.toLowerCase() === "approved"
          );
          setHasApprovedResignation(hasApproved);

          setResignationData(formattedData);
        } else {
          setSnackMessage("Failed to fetch resignation data.");
          setSnackColor("error");
          setSnackOpen(true);
        }
      } catch (error) {
        if (error.response) {
          if (
            error.response.status === 400 ||
            error.response.status === 401 ||
            error.response.status === 403
          ) {
            setSnackMessage(error.response.data.message);
            setSnackColor("error");
            setSnackOpen(true);
          }
        } else {
          setSnackMessage("An error occurred while fetching resignation data.");
          setSnackColor("error");
          setSnackOpen(true);
        }
      }
    };

    fetchResignationData();
  }, [empId, newResignationSubmitted]);

  const isSendButtonDisabled = !reason.trim();

  const exitClearanceViewDownloadDisabled =
    !selectedExitClearanceRow?.exitClearanceStatus ||
    selectedExitClearanceRow?.exitClearanceStatus === "Not Started";

  const exitInterviewViewDownloadDisabled =
    !selectedExitInterviewRow?.exitInterviewStatus ||
    selectedExitInterviewRow?.exitInterviewStatus === "Not Started";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={3} mb={3}>
        <Grid container spacing={1} justifyContent="start">
          <Grid item xs={12} md={4} lg={3}>
            <Card sx={{ height: "auto", maxHeight: "400px" }}>
              <MDBox p={2}>
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ fontFamily: properties.fontFamily }}
                >
                  Apply For Resignation
                </MDTypography>
              </MDBox>

              <MDBox component="form" role="form" onSubmit={handleSubmit}>
                <MDBox pt={1} pb={2} px={3}>
                  <MDBox mb={2}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        format="DD/MM/YYYY"
                        sx={{ width: "100%" }}
                        size="small"
                        label="Date"
                        value={value}
                        onChange={newValue => setValue(newValue)}
                        readOnly
                      />
                    </LocalizationProvider>
                  </MDBox>

                  <MDBox mb={2}>
                    <MDInput
                      fullWidth
                      type="text"
                      label={
                        <span>
                          Reason<span style={{ color: properties.colors.Red }}>*</span>
                        </span>
                      }
                      variant="standard"
                      value={reason}
                      onChange={e => setReason(e.target.value)}
                      error={reasonError}
                    />
                    {reasonError && <FormHelperText error>Reason is required</FormHelperText>}
                  </MDBox>

                  <Divider />

                  <MDBox mt={2} display="flex" justifyContent="center">
                    <MDButton
                      type="submit"
                      variant="gradient"
                      color="info"
                      sx={{
                        width: "18ch",
                        background: properties.colors.bgcolorbluethem,
                        fontSize: "0.85rem",
                      }}
                      disabled={isSendButtonDisabled}
                    >
                      Send
                    </MDButton>
                  </MDBox>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>


          <Grid item xs={12} md={8} lg={9}>
            <Card sx={{ height: "auto", overflowX: "hidden" }}>
              <MDBox p={2}>
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ fontFamily: properties.fontFamily }}
                >
                  Resignation Requests
                </MDTypography>
              </MDBox>

              <MDBox p={2}>
                <Paper sx={{ width: "100%", overflowX: "hidden" }}>
                  <DataGrid
                    rows={resignationData}
                    columns={dynamicColumns}
                    pageSize={5}
                    autoHeight
                    pagination
                    disableSelectionOnClick
                    sx={{
                      fontSize: "14px",
                      fontFamily: properties.fontFamily,
                      "& .MuiDataGrid-virtualScroller": { overflowX: "hidden" },
                    }}
                  />
                </Paper>
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        {/* Exit Clearance Menu */}
        <Menu
          anchorEl={exitClearanceAnchorEl}
          open={openExitClearanceMenu}
          onClose={handleCloseExitClearanceMenu}
          PaperProps={{
            elevation: 3,
            sx: { minWidth: 200 }
          }}
        >
          <MenuItem
            onClick={handleViewExitClearance}
          // disabled={exitClearanceViewDownloadDisabled
          >
            <ListItemIcon>
              <ArticleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View Clearance</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleFillExitClearance}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {selectedExitClearanceRow?.exitClearanceStatus === "Not Started"
                ? "Fill Clearance"
                : "Update Clearance"}
            </ListItemText>
          </MenuItem>
          {/* <MenuItem
            onClick={handleDownloadExitClearance}
          // disabled={exitClearanceViewDownloadDisabled}
          >
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Download PDF</ListItemText>
          </MenuItem> */}
        </Menu>

        {/* Exit Interview Menu */}
        <Menu
          anchorEl={exitInterviewAnchorEl}
          open={openExitInterviewMenu}
          onClose={handleCloseExitInterviewMenu}
          PaperProps={{
            elevation: 3,
            sx: { minWidth: 200 }
          }}
        >
          <MenuItem
            onClick={handleViewExitInterview}
          // disabled={exitInterviewViewDownloadDisabled}
          >
            <ListItemIcon>
              <QuizIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View Interview</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleFillExitInterview}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {selectedExitInterviewRow?.exitInterviewStatus === "Not Started"
                ? "Fill Interview"
                : "Update Interview"}
            </ListItemText>
          </MenuItem>
          {/* <MenuItem
            onClick={handleDownloadExitInterview}
          // disabled={exitInterviewViewDownloadDisabled}
          >
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Download PDF</ListItemText>
          </MenuItem> */}
        </Menu>

        {/* View Resignation Details Modal */}
        <Dialog
          open={openViewModal}
          onClose={() => setOpenViewModal(false)}
          fullWidth
          sx={{
            "& .MuiDialog-paper": {
              width: "700px",
              maxWidth: "none"
            }
          }}
        >
          <DialogTitle
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            <MDTypography variant="h6" fontFamily={properties.fontFamily}>
              Resignation Details
            </MDTypography>
          </DialogTitle>
          <DialogContent sx={{ padding: 2 }}>
            {selectedResignationRequest && (
              <form>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextField
                      label="Joining Date"
                      value={selectedResignationRequest.joinDate || "No Join Date Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Employee ID"
                      value={selectedResignationRequest.empId || "No ID Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Employee Name"
                      value={selectedResignationRequest.empName || "No Name Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Applied Date"
                      value={selectedResignationRequest.applyDate || "No Date Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Relieving Date"
                      value={
                        selectedResignationRequest.relievingDate || "No Relieving Date Provided"
                      }
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Resignation Notice"
                      value={selectedResignationRequest.resignationNotice || "No Notice Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Status"
                      value={selectedResignationRequest.status || "No status available"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Reason"
                      value={selectedResignationRequest.reason || "No reason provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                </Grid>
              </form>
            )}
          </DialogContent>
          <DialogActions>
            <MDButton color="secondary" onClick={() => setOpenViewModal(false)} variant="contained">
              Close
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* Revoke Resignation Modal */}
        <Dialog
          open={openRevokeModal}
          onClose={() => setOpenRevokeModal(false)}
          fullWidth
          sx={{
            "& .MuiDialog-paper": {
              width: "700px",
              maxWidth: "none"
            }
          }}
        >
          <DialogTitle
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            <MDTypography variant="h6">Revoke Resignation</MDTypography>
          </DialogTitle>
          <DialogContent sx={{ padding: 2 }}>
            {selectedResignationRequest && (
              <form onSubmit={handleRevokeSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextField
                      label="Employee ID"
                      value={selectedResignationRequest.empId || "No ID Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Employee Name"
                      value={selectedResignationRequest.empName || "No Name Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Resignation Notice"
                      value={selectedResignationRequest.resignationNotice || "No Notice Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Applied Date"
                      value={selectedResignationRequest.applyDate || "No Date Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label="Relieving Date"
                      value={
                        selectedResignationRequest.relievingDate || "No Relieving Date Provided"
                      }
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label={
                        <span>
                          Reason for Revocation<span style={{ color: properties.colors.Red }}>*</span>
                        </span>
                      }
                      value={revokeReason}
                      onChange={e => setRevokeReason(e.target.value)}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      error={revokeReasonError}
                      helperText={revokeReasonError ? "Please provide a reason." : ""}
                    />
                  </Grid>
                </Grid>
                <DialogActions>
                  <MDButton
                    color="secondary"
                    onClick={() => setOpenRevokeModal(false)}
                    variant="contained"
                  >
                    Cancel
                  </MDButton>
                  <MDButton type="submit" color="error" variant="contained">
                    Revoke
                  </MDButton>
                </DialogActions>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </MDBox>

      <Snackbar
        open={snackOpen}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={6000}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          close={handleClose}
          severity={snackColor == "success" ? "success" : "error"}
          variant="filled"
          sx={{ width: "100%", color: "#fff" }}
        >
          {snackMessage}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}